<template>
  <div v-if="showPopup" :class="$style.popup">
    <div :class="$style.cover"></div>

    <div :class="$style.popup_box">
      <div ref="wrapperRef" id="popup-wrapper" :class="$style.wrapper">
        <!-- Title -->
        <p
          v-if="msg.title"
          :class="$style.title"
          :style="msg.titleStyle || {}"
          :title="msg.title"
        >
          {{ msg.title }}
          <i v-if="msg.close" @click="cancel">✖</i>
        </p>

        <!-- Text -->
        <div :class="$style.text" v-html="msg.text"></div>

        <!-- Buttons -->
        <div :class="$style.btnPanel" :style="msg.btnStyle.panel">
          <!-- Cancel -->
          <span
            v-if="msg.btn.cancel"
            :class="$style.btnItem"
            :style="styleOrDefault(msg.btnStyle.cancel)"
          >
            <span
              :class="[$style.btn, $style.cancel]"
              :style="{ width: buttonCount === 1 ? '100%' : '' }"
              @click="cancel"
            >
              {{ msg.btn.cancel }}
            </span>
          </span>

          <!-- Apply -->
          <span
            v-if="msg.btn.apply"
            :class="$style.btnItem"
            :style="styleOrDefault(msg.btnStyle.apply)"
          >
            <span
              :class="[$style.btn, $style.apply]"
              :style="{ width: buttonCount === 1 ? '100%' : '' }"
              @click="apply"
            >
              {{ msg.btn.apply }}
            </span>
          </span>

          <!-- Confirm -->
          <span
            v-if="msg.btn.confirm"
            :class="$style.btnItem"
            :style="styleOrDefault(msg.btnStyle.confirm)"
          >
            <span
              :class="[$style.btn, $style.confirm]"
              :style="{ width: buttonCount === 1 ? '100%' : '' }"
              @click="confirm"
            >
              {{ msg.btn.confirm }}
            </span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeMount,
  nextTick,
} from 'vue'

export default defineComponent({
  name: 'Popup',

  props: {
    msg: {
      type: Object,
      required: true,
    },
  },

  emits: ['cancel', 'apply', 'confirm'],

  setup(props, { emit, expose }) {
    const showPopup = ref(true)
    const buttonCount = ref(1)
    const wrapperRef = ref(null)

    const defaultCss = { width: '100%' }

    /* ---------- lifecycle ---------- */

    onMounted(async () => {
      await nextTick()
      adjustPosition()
    })

    /* ---------- methods ---------- */

    const cancel = (res) => emit('cancel', res)
    const apply = (res) => emit('apply', res)
    const confirm = (res, event) => emit('confirm', res, event)

    const close = () => {
      showPopup.value = false
    }

    const countButtons = (btn = {}) =>
      Object.values(btn).filter(Boolean).length

    const styleOrDefault = (style) =>
      style && Object.keys(style).length ? style : defaultCss

    const adjustPosition = () => {
      const el = wrapperRef.value
      if (!el) return

      let position = 'middle'
      const p = props.msg.position

      if (typeof p === 'number') {
        position = `calc(50% - ${p}px)`
      } else if (
        typeof p === 'string' &&
        (p.includes('%') || p.includes('vh'))
      ) {
        position = p
      } else {
        switch (p) {
          case 'top':
            position = '0px'
            break
          case 'middleTop':
            position = `calc(50% - ${el.offsetHeight}px)`
            break
          case 'middle':
            position = `calc(50% - ${el.offsetHeight / 2}px)`
            break
          case 'middleBottom':
            position = '50%'
            break
          case 'bottom':
            position = '100%'
            break
        }
      }

    /* ---------- watch ---------- */

    watch(
      () => showPopup.value,
      (visible) => {
        buttonCount.value = countButtons(props.msg.btn)

        if (visible) {
          document.body.style.position = 'fixed'
          document.body.style.width = '100%'
        } else {
          document.body.style.position = ''
          document.body.style.width = ''
        }
      },
      { immediate: true }
    )

      el.style.top = position
    }

    /* ---------- expose ---------- */
    // createVNode / render から close() を呼べるようにする
    expose({ close })

    return {
      showPopup,
      buttonCount,
      wrapperRef,
      cancel,
      apply,
      confirm,
      styleOrDefault,
    }
  },
})
</script>

<style lang="scss" module>
.popup {
  position: fixed;
  inset: 0;
  z-index: 20001;

  .cover {
    width: 100%;
    height: 100%;
    background: #000;
    opacity: 0.6;
  }

  .popup_box {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .wrapper {
    position: fixed;
    width: 30%;
    min-width: 350px;
    max-height: 80%;
    padding: 15px;
    overflow: auto;
    background: #fff;
    border-radius: 10px;
    box-sizing: border-box;
    left: 0;
    right: 0;
    margin: auto;

    .title {
      position: relative;
      height: 35px;
      padding-bottom: 10px;
      font-weight: bold;
      border-bottom: 1px solid #eee;

      i {
        position: absolute;
        top: -10px;
        right: 0;
        padding: 10px;
        cursor: pointer;
      }
    }

    .text {
      max-height: calc(80% - 110px);
      padding: 10px 0;
      overflow-y: auto;
      white-space: pre-wrap;
    }

    .btnPanel {
      margin-top: 10px;
      text-align: center;

      .btnItem {
        display: inline-block;
        text-align: center;

        .btn {
          width: 90%;
          height: 35px;
          line-height: 35px;
          border: 1px solid #070141;
          border-radius: 5px;
          cursor: pointer;
          display: inline-block;
        }

        .cancel {
          color: #070141;
        }

        .confirm {
          color: #fff;
          background: #070141;
        }
      }
    }
  }
}

@media screen and (max-width: 767px) {
  .popup {
    .wrapper {
      width: 90%;
      min-width: auto;
    }
  }
}
</style>
